import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { GRAZING_IMAGE } from "@/lib/images";
import { useSite } from "@/lib/SiteContext";

const PRODUCT_OPTIONS = ["Brownies & blondies", "Cookies & biscuits", "Cookie dough", "Mixed boxes", "Seasonal specials", "Grazing table"];
const EVENT_TYPES = ["Birthday", "Wedding", "Corporate / office", "Other"];

const inputClass =
  "w-full rounded-xl border border-choco-line bg-choco px-4 py-2.5 text-sm text-cream-text placeholder:text-cream-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus";

const Field = ({ id, label, children }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-cream-text">
      {label}
    </label>
    {children}
  </div>
);

export default function Events() {
  const { site } = useSite();
  const c = site?.content?.events || {};
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_date: "", venue_or_postcode: "",
    guest_count: "", event_type: "", products_of_interest: [], dietary_requirements: "", budget: "", details: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleProduct = (p) =>
    setForm((f) => ({
      ...f,
      products_of_interest: f.products_of_interest.includes(p)
        ? f.products_of_interest.filter((x) => x !== p)
        : [...f.products_of_interest, p],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/public/event-enquiry", {
        ...form,
        event_date: form.event_date || null,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
      });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid="events-page">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Events &amp; Grazing Tables</p>
          <h1 className="mt-3 font-serif text-3xl font-bold tracking-tight text-cream-text sm:text-4xl lg:text-5xl">
            {c.heading || "Sweet-treat grazing tables, made for your event"}
          </h1>
          <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-cream-muted">
            {c.body || "Birthdays, weddings, office gatherings — our curated grazing tables and celebration bakes are made to impress. Send us an enquiry and we'll come back to you with ideas and a tailored quote."}
          </p>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-cream-muted">
            {c.enquiry_note || "Event orders are quoted individually — there's nothing to pay online."}
          </p>
          <div className="mt-8 overflow-hidden rounded-3xl border border-choco-line">
            <img src={GRAZING_IMAGE.url} alt={GRAZING_IMAGE.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {done ? (
            <div className="flex h-full flex-col items-center justify-center rounded-3xl bg-cream p-10 text-center text-cream-darktext" data-testid="enquiry-success-panel">
              <CheckCircle2 className="h-12 w-12 text-gold" aria-hidden="true" />
              <h2 className="mt-5 font-serif text-2xl font-bold">Thank you — enquiry received!</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-cream-darkmuted">
                We've got your details and will be in touch shortly to chat about your event.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 rounded-3xl border border-choco-line bg-choco-light p-6 sm:p-8" noValidate data-testid="enquiry-form">
              <h2 className="font-serif text-xl font-semibold text-cream-text">Tell us about your event</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field id="enq-name" label="Your name *">
                  <input id="enq-name" required value={form.name} onChange={set("name")} className={inputClass} data-testid="enquiry-name-input" />
                </Field>
                <Field id="enq-email" label="Email *">
                  <input id="enq-email" type="email" required value={form.email} onChange={set("email")} className={inputClass} data-testid="enquiry-email-input" />
                </Field>
                <Field id="enq-phone" label="Telephone *">
                  <input id="enq-phone" type="tel" required value={form.phone} onChange={set("phone")} className={inputClass} data-testid="enquiry-phone-input" />
                </Field>
                <Field id="enq-date" label="Event date">
                  <input id="enq-date" type="date" value={form.event_date} onChange={set("event_date")} className={inputClass} data-testid="enquiry-date-input" />
                </Field>
                <Field id="enq-venue" label="Venue or postcode">
                  <input id="enq-venue" value={form.venue_or_postcode} onChange={set("venue_or_postcode")} className={inputClass} data-testid="enquiry-venue-input" />
                </Field>
                <Field id="enq-guests" label="Estimated guest count">
                  <input id="enq-guests" type="number" min="1" value={form.guest_count} onChange={set("guest_count")} className={inputClass} data-testid="enquiry-guests-input" />
                </Field>
              </div>

              <fieldset>
                <legend className="mb-2 text-sm font-semibold text-cream-text">Type of event</legend>
                <RadioGroup value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))} className="flex flex-wrap gap-4">
                  {EVENT_TYPES.map((t) => (
                    <div key={t} className="flex items-center gap-2">
                      <RadioGroupItem value={t} id={`enq-type-${t}`} className="border-choco-line text-gold" data-testid={`enquiry-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
                      <label htmlFor={`enq-type-${t}`} className="text-sm text-cream-muted">{t}</label>
                    </div>
                  ))}
                </RadioGroup>
              </fieldset>

              <fieldset>
                <legend className="mb-2 text-sm font-semibold text-cream-text">Products of interest</legend>
                <div className="grid grid-cols-2 gap-3">
                  {PRODUCT_OPTIONS.map((p) => (
                    <div key={p} className="flex items-center gap-2">
                      <Checkbox
                        id={`enq-prod-${p}`}
                        checked={form.products_of_interest.includes(p)}
                        onCheckedChange={() => toggleProduct(p)}
                        className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco"
                        data-testid={`enquiry-product-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      />
                      <label htmlFor={`enq-prod-${p}`} className="text-sm text-cream-muted">{p}</label>
                    </div>
                  ))}
                </div>
              </fieldset>

              <Field id="enq-dietary" label="Dietary or allergen requirements">
                <textarea id="enq-dietary" rows={2} value={form.dietary_requirements} onChange={set("dietary_requirements")} className={inputClass} data-testid="enquiry-dietary-input" />
              </Field>
              <Field id="enq-budget" label="Approximate budget">
                <input id="enq-budget" value={form.budget} onChange={set("budget")} placeholder="e.g. £150–£250" className={inputClass} data-testid="enquiry-budget-input" />
              </Field>
              <Field id="enq-details" label="Anything else we should know?">
                <textarea id="enq-details" rows={4} value={form.details} onChange={set("details")} className={inputClass} data-testid="enquiry-details-input" />
              </Field>

              {error && (
                <p className="text-sm font-semibold text-red-400" role="alert" data-testid="enquiry-error-message">{error}</p>
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-gold px-8 py-3.5 text-base font-bold text-choco transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco active:scale-[0.98] disabled:opacity-60"
                data-testid="enquiry-submit-button"
              >
                {busy ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
